import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Settings, Loader2, Download } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import {
  DataTable, Drawer, FilterBar, Segmented, Pager, Loading, useDebounced, fdate, inputCls, btnGold, btnGhost,
} from '@/components/dashboard/kit';
import { PersonCell, PersonChips, TIER, TIER_OPTIONS, TEMP, STAGE_FILTER, eventTitle, plural } from './people';
import PersonDrawer from './PersonDrawer';
import NetworkTree from './NetworkTree';
import BotSuspects from './BotSuspects';

/*
  Personas (decisión de Samuel, 10/10/2026): una sola ficha por persona (email), aunque haya hecho el test,
  tenga cuenta y se haya inscrito a eventos. Buscador global arriba y cuatro listas:
  Todas · Usuarios (con cuenta) · Test de idoneidad · Inscripciones (una fila por inscripción) · Red (árbol
  completo de patrocinio, plataforma y CDM). Cada lista se descarga en CSV con sus filtros (todas las páginas).
  Filas compactas (nombre, email, teléfono y estado); al pulsar, la ficha completa en el panel lateral.
  Los datos llegan paginados del servidor (routes/admin_people.py).
*/
export const PEOPLE_SUBS = [['todas', 'all', 'Todas'], ['usuarios', 'users', 'Usuarios'], ['tests', 'tests', 'Test de idoneidad'],
  ['inscripciones', 'registrations', 'Inscripciones'], ['red', 'tree', 'Red']];
const PAGE_SIZE = 30;

const eventLabel = (e) => `${eventTitle(e.kind, e.title)}${e.date ? ` · ${fdate(e.date)}` : ''} (${e.count})`;

export default function PeopleSection({ sub, onSub, personKey, onOpen, onClose, eventKey, onEvent, initialTier }) {
  const kind = (PEOPLE_SUBS.find(([k]) => k === sub) || PEOPLE_SUBS[0])[1];
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 250);
  const [filters, setFilters] = useState({ tier: initialTier || '', test: '', temp: '', stage: '', referrer: '', paid: '' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rev, setRev] = useState(0);
  const [settings, setSettings] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Al cambiar de lista o de filtros se vuelve a la primera página
  useEffect(() => { setPage(1); }, [kind, query, filters, eventKey]);

  // Mismos filtros para la lista y para el CSV
  const params = useCallback(() => {
    const p = { kind, q: query, referrer: filters.referrer };
    if (kind === 'registrations') Object.assign(p, { event: eventKey, paid: filters.paid });
    else Object.assign(p, { tier: filters.tier });
    if (kind === 'all') p.test = filters.test;
    if (kind === 'tests') Object.assign(p, { temp: filters.temp, stage: filters.stage });
    return p;
  }, [kind, query, filters, eventKey]);

  useEffect(() => {
    if (kind === 'tree') return undefined;
    let alive = true;
    setLoading(true);
    dashboardApi.adminPeople({ ...params(), page, page_size: PAGE_SIZE })
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((e) => { if (alive) setError(e.message || 'No se han podido cargar las personas'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, params, page, rev]);

  const download = async () => {
    setExporting(true);
    try { toast.success(`Descargado: ${await dashboardApi.adminPeopleExport(params())}`); } catch (e) { toast.error(e.message); } finally { setExporting(false); }
  };

  const reload = useCallback(() => setRev((r) => r + 1), []);
  const closeSettings = useCallback(() => setSettings(false), []);
  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const counts = data?.counts || {};
  const tiers = data?.tiers || {};
  const refOptions = [['', 'Traído por: cualquiera'], ...(data?.referrers || []).map((r) => [r.value, `${r.name} (${r.count})`])];
  const filterDefs = [];
  if (kind === 'all' || kind === 'users' || kind === 'tests') {
    filterDefs.push({ key: 'tier', label: 'Nivel', value: filters.tier,
      options: [['', 'Todos los niveles'], ...TIER_OPTIONS.map((t) => [t, `${TIER[t][0]} (${tiers[t] ?? 0})`])] });
  }
  if (kind === 'all') filterDefs.push({ key: 'test', label: 'Test', value: filters.test, options: [['', 'Con y sin test'], ['si', 'Con test'], ['no', 'Sin test']] });
  if (kind === 'tests') {
    filterDefs.push({ key: 'temp', label: 'Temperatura', value: filters.temp,
      options: [['', 'Todas las temperaturas'], ...Object.entries(TEMP).map(([k, [l]]) => [k, l])] });
    filterDefs.push({ key: 'stage', label: 'Etapa', value: filters.stage, options: STAGE_FILTER });
  }
  if (kind === 'registrations') {
    filterDefs.push({ key: 'event', label: 'Evento o bootcamp', value: eventKey || '',
      options: [['', 'Todos los eventos y bootcamps'], ...(data?.events || []).map((e) => [e.key, eventLabel(e)])] });
    filterDefs.push({ key: 'paid', label: 'Pago', value: filters.paid, options: [['', 'Pagados y pendientes'], ['si', 'Pagados'], ['no', 'Pendientes']] });
  }
  if (kind !== 'registrations' && kind !== 'tree') filterDefs.push({ key: 'referrer', label: 'Traído por', value: filters.referrer, options: refOptions });
  const onFilter = (k, v) => (k === 'event' ? onEvent(v) : setFilter(k, v));

  const selectedEvent = kind === 'registrations' && eventKey ? (data?.events || []).find((e) => e.key === eventKey) : null;

  const cols = [
    { key: 'name', label: 'Persona', primary: true, noSort: true, render: (r) => <PersonCell name={r.name} email={r.email} /> },
    { key: 'phone', label: 'Teléfono', noSort: true, mobile: true, className: 'text-gray-300 whitespace-nowrap', render: (r) => r.phone || '—' },
    kind === 'registrations'
      ? { key: 'event', label: 'Inscripción', noSort: true, mobile: true,
        render: (r) => <span className="text-gray-300">{eventTitle(r.registration.kind, r.registration.title)}<span className="block text-xs text-gray-500">{fdate(r.registration.date)}</span></span> }
      : kind === 'tests'
        ? { key: 'test_at', label: 'Test', noSort: true, hideSm: true, className: 'text-gray-400 whitespace-nowrap', render: (r) => fdate(r.test_at) }
        : { key: 'referrer', label: 'Traído por', noSort: true, hideSm: true, className: 'text-gray-400', render: (r) => r.referrer?.name || '—' },
    { key: 'status', label: 'Estado', noSort: true, mobile: true, render: (r) => <PersonChips row={r} /> },
  ];

  return (
    <div className="space-y-4" data-testid="people-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented tabs={PEOPLE_SUBS.map(([k, apiKind, label]) => [k, label, counts[apiKind]])} value={sub} onChange={onSub} />
        {kind === 'tests' && (
          <button type="button" onClick={() => setSettings(true)} className={`${btnGhost} h-10`}><Settings size={14} /> Ajustes del test</button>
        )}
      </div>

      <FilterBar search={q} onSearch={setQ} filters={filterDefs} onFilter={onFilter}
        placeholder={kind === 'tree' ? 'Buscar en la red por nombre o email' : undefined}
        right={kind === 'tree' ? null : (<>
          <button type="button" onClick={download} disabled={exporting || !data?.total} className={`${btnGhost} h-10`} data-testid="people-export">
            {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Descargar CSV
          </button>
          {loading && data && <Loader2 size={16} className="animate-spin text-gold self-center" aria-label="Cargando" />}
        </>)} />

      {(kind === 'all' || kind === 'users') && <BotSuspects onChanged={reload} />}

      {selectedEvent && (
        <p className="text-sm text-gray-400" data-testid="event-summary">
          <span className="text-white font-medium">{eventTitle(selectedEvent.kind, selectedEvent.title)}</span>
          {selectedEvent.date && ` · ${fdate(selectedEvent.date)}`} · {plural(selectedEvent.count, 'inscrito', 'inscritos')}
          {' · '}<span className="text-emerald-300">{plural(selectedEvent.paid, 'pagado o incluido', 'pagados o incluidos')}</span>
          {' · '}<span className="text-amber-200">{plural(selectedEvent.pending, 'pendiente', 'pendientes')}</span>
        </p>
      )}

      {kind === 'tree' ? <NetworkTree query={q} onOpen={onOpen} /> : !data ? <Loading error={error} /> : (
        <>
          {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
          <DataTable cols={cols} rows={data.items} rowKey="row_id" pageSize={PAGE_SIZE + 1} onRow={(r) => onOpen(r.key)}
            empty={query ? `Nadie coincide con «${query}».` : 'No hay personas con estos filtros.'} />
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
        </>
      )}

      <PersonDrawer personKey={personKey} onClose={onClose} onOpen={onOpen} onChanged={reload} />
      <TestSettings open={settings} onClose={closeSettings} />
    </div>
  );
}

// Ajustes del test: enlace de la agenda tras el test (por defecto, el Calendly de Toni). Campo interno: calendly_url.
function TestSettings({ open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCfg(null);
    dashboardApi.adminFunnelConfig()
      .then((c) => setCfg({ calendly_url: c.calendly_url || '', whatsapp_number: c.whatsapp_number || '' }))
      .catch((e) => { toast.error(e.message || 'No se han podido cargar los ajustes'); onClose(); });
  }, [open, onClose]);
  const save = async () => {
    setSaving(true);
    try {
      await dashboardApi.adminUpdateFunnelConfig(cfg);
      toast.success('Ajustes guardados');
      onClose();
    } catch (e) { toast.error(e.message || 'No se han podido guardar'); } finally { setSaving(false); }
  };
  return (
    <Drawer open={open} onClose={onClose} title="Ajustes del test de idoneidad"
      footer={cfg && (<>
        <button type="button" onClick={onClose} className={`${btnGhost} h-10`}>Cancelar</button>
        <button type="button" onClick={save} disabled={saving} className={`${btnGold} h-10`}>{saving ? 'Guardando…' : 'Guardar'}</button>
      </>)}>
      {!cfg ? <Loading /> : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Enlace de la agenda (Calendly)</span>
            <input value={cfg.calendly_url} onChange={(e) => setCfg({ ...cfg, calendly_url: e.target.value })}
              placeholder="https://calendly.com/…" className={`${inputCls} mt-1 w-full h-10 font-mono`} />
            <span className="block text-xs text-gray-400 mt-1">Al terminar el test, la persona elige día y hora en esta agenda.</span>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Número de WhatsApp (próximamente)</span>
            <input value={cfg.whatsapp_number} onChange={(e) => setCfg({ ...cfg, whatsapp_number: e.target.value })}
              placeholder="+34600000000" className={`${inputCls} mt-1 w-full h-10`} />
          </label>
        </div>
      )}
    </Drawer>
  );
}
